import React, { useEffect, useRef, useState } from 'react';
import { Download, FileText, Image, Play, X } from 'lucide-react';
import whatsappAPI from '../../services/whatsapp';
import WhatsAppAudioPlayer from './WhatsAppAudioPlayer';

const WhatsAppMessageMedia = ({ message, onLoad }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(0);
  const preview = useRef(null);
  const mimeType = message?.media_mime_type || message?.mediaMimeType || '';
  const filename = message?.media_filename || message?.mediaFilename || 'Attachment';
  const type = message?.type || '';

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    const load = async () => {
      if (message?.localMediaUrl) { setUrl(message.localMediaUrl); return; }
      if ((!requested && !['image', 'audio', 'sticker'].includes(type)) || !message?.id || !['image', 'audio', 'video', 'document', 'sticker'].includes(type)) return;
      setUrl('');
      setLoading(true);
      setError('');
      try {
        const { data } = await whatsappAPI.getMedia(message.id);
        objectUrl = URL.createObjectURL(data);
        if (active) setUrl(objectUrl); else URL.revokeObjectURL(objectUrl);
      } catch (err) {
        if (active) setError('Could not load attachment.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [message?.id, message?.localMediaUrl, type, requested]);

  if (!['image', 'audio', 'video', 'document', 'sticker'].includes(type)) return null;
  if (loading) {
    return <div role="status" aria-label="Loading attachment">
      {type === 'sticker' ? (
        <div aria-hidden="true" className="h-36 w-36 max-w-full rounded-lg bg-gray-100 motion-safe:animate-pulse" />
      ) : type === 'image' || type === 'video' ? (
        <div aria-hidden="true" className={`flex w-60 max-w-full items-center justify-center rounded-lg bg-gray-100 motion-safe:animate-pulse ${type === 'video' ? 'aspect-video' : 'aspect-[4/3]'}`}>
          <Image className="h-8 w-8 text-gray-300" />
        </div>
      ) : type === 'audio' ? (
        <div aria-hidden="true" className="flex h-16 w-60 max-w-full items-center gap-3 py-1 motion-safe:animate-pulse">
          <div className="h-11 w-11 shrink-0 rounded-full bg-gray-200" />
          <div className="min-w-0 flex-1 space-y-3">
            <div className="h-1.5 rounded-full bg-gray-200" />
            <div className="flex justify-between"><div className="h-2 w-6 rounded bg-gray-100" /><div className="h-2 w-6 rounded bg-gray-100" /></div>
          </div>
        </div>
      ) : (
        <div aria-hidden="true" className="flex h-11 w-52 max-w-full items-center gap-3 rounded-lg bg-gray-100 px-3 motion-safe:animate-pulse">
          <FileText className="h-5 w-5 shrink-0 text-gray-300" />
          <div className="h-2 w-28 rounded bg-gray-200" />
        </div>
      )}
    </div>;
  }
  if (!url && type === 'video') return <div className="mt-1">
    <button type="button" onClick={() => setRequested(value => value + 1)} className="flex aspect-video w-full min-w-52 flex-col items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2">
      <span aria-hidden="true" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20"><Play className="ml-0.5 h-5 w-5 fill-current" /></span>
      <span className="text-sm font-medium">{error ? 'Retry video' : 'Load video'}</span>
    </button>
    {error && <p role="alert" className="mt-1 text-xs text-red-800">{error}</p>}
  </div>;
  if (!url) return <div className="mt-2">{error && <p role="alert" className="text-xs text-red-800">{error}</p>}<button type="button" onClick={() => setRequested(value => value + 1)} className="rounded-lg border bg-white px-3 py-2 text-sm text-gray-700">{error ? 'Retry attachment' : `Load ${type === 'audio' ? 'voice note' : type === 'document' ? filename : type}`}</button></div>;
  if (type === 'sticker') return <img src={url} alt="Sticker" onLoad={onLoad} onError={() => { setUrl(''); setError('Could not load sticker.'); }} className="block h-36 w-36 max-w-full object-contain" />;
  if (type === 'image') return <>
    <button type="button" aria-label={`Preview image: ${filename}`} onClick={() => preview.current.showModal()} className="block w-full cursor-zoom-in overflow-hidden rounded-lg">
      <img onLoad={onLoad} src={url} alt={filename} className="block max-h-80 w-full rounded-lg object-cover" />
    </button>
    <dialog ref={preview} aria-label={`Image preview: ${filename}`} onClick={event => { if (event.target === event.currentTarget) preview.current.close(); }}
      className="fixed inset-0 m-auto max-h-[100dvh] w-full max-w-none overflow-auto border-0 bg-transparent p-4 backdrop:bg-black/85">
      <div className="pointer-events-none flex min-h-[85dvh] items-center justify-center">
        <img src={url} alt={filename} className="pointer-events-auto max-h-[85dvh] max-w-full rounded-lg object-contain" />
      </div>
      <button type="button" autoFocus aria-label="Close image preview" onClick={() => preview.current.close()} className="fixed right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-700">
        <X aria-hidden="true" className="h-5 w-5" />
      </button>
    </dialog>
  </>;
  if (type === 'audio') return <WhatsAppAudioPlayer key={url} url={url} onLoad={onLoad} />;
  if (type === 'video') return <video src={url} controls playsInline preload="metadata" onLoadedMetadata={onLoad} className="mt-1 aspect-video w-full rounded-lg bg-black object-contain" />;
  return (
    <a href={url} download={filename} className="mt-2 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
      <FileText className="w-4 h-4" />
      <span className="max-w-[12rem] truncate">{filename || mimeType || 'Download file'}</span>
      <Download className="w-4 h-4" />
    </a>
  );
};

export default WhatsAppMessageMedia;
